import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { ReactSVG } from 'react-svg';
import { useSpring, animated, easings } from '@react-spring/web';
import { Textfit } from 'react-textfit';
import { kidInstrumentAssets } from '../../../../utils/RoundUpUtils';
import triangle_star from '../../assets/triangle_star.svg';
import dash_star from '../../assets/dash_star.svg';
import sunRays from '../../assets/backgrounds/high_sun_rays.svg';
import multi_color_star_1 from '../../assets/multi_color_star_1.svg';
import multi_color_star_2 from '../../assets/multi_color_star_2.svg';
import multi_color_star_3 from '../../assets/multi_color_star_3.svg';
import '../Slides.scss';
import './BandRoleSlide.scss';

const BandRoleSlide = ({ data }) => {
  const {
    kid_name,
    band_role,
    band_name,
    instrument
  } = data;

  const [fadeInDone, setFadeInDone] = useState(false);
  
  const instrumentIcon = kidInstrumentAssets[instrument];


  const instrumentSpring = useSpring({
    from: { opacity: 0, transform: 'scale(0)' },
    to: { opacity: 1, transform: 'scale(1)' },
    delay: 400,
    config: {
      tension: 180,
      friction: 12,
    },
  });

  const kidNameSpring = useSpring({
    from: { opacity: 0, transform: 'translateY(100vh)' },
    to: { opacity: 1, transform: 'translateY(0px)' },
    delay: 200,
    config: {
      tension: 180,  // lower tension = more wobble
      friction: 12,  // lower friction = 'less damping'
    },
  });

  const bandRoleSpring = useSpring({
    from: { opacity: 0, transform: 'translateY(20px)' },
    to: { opacity: 1, transform: 'translateY(0px)' },
    config: { tension: 200, friction: 25 },
    delay: 400,
  });

  const textSpring = useSpring({
    from: { opacity: 0, transform: 'translateX(100px)' },
    to: { opacity: 1, transform: 'translateX(0px)' },
    config: {
      tension: 180,
      friction: 12,
    },
    delay: 600,
  });

  const boxSpring = useSpring({
    from: { opacity: 0, transform: 'translateY(100vh)' },
    to: { opacity: 1, transform: 'translateY(0px)' },
    delay: 800,
  });

  const bandNameSpring = useSpring({
    from: { opacity: 0, transform: 'scale(2)' },
    to: { opacity: 1, transform: 'scale(1)' },
    delay: 1200,
    config: { tension: 250, friction: 15 },
  });

  const sunRaysFadeInSpring = useSpring({
    from: { opacity: 0 },
    to: { opacity: 1 },
    config: { duration: 1000 },
    delay: 1000,
    onRest: () => setFadeInDone(true),
  });

  const sunRaysScaleSpring = useSpring({
    from: { scale: 1 },
    to: { scale: 1.1 },
    loop: fadeInDone ? { reverse: true } : false,
    config: { duration: 1500, easing: easings.easeInOutSine },
  });

  const sunRaysSpring = {
    ...sunRaysFadeInSpring,
    ...sunRaysScaleSpring,
  };


  // star sparkles

  const appear = useSpring({
    from: { opacity: 0 },
    to: { opacity: 1 }
  })

  const spinSparkle = useSpring({
    from: { transform: 'rotate(0deg)' },
    to: { transform: 'rotate(360deg)' },
    loop: true,
    config: { duration: 3000 },
  });

  const pulseSparkle = useSpring({
    from: { transform: 'scale(1)' },
    to: { transform: 'scale(1.3)' },
    loop: { reverse: true },
  });
  
  const twinkleSparkle = useSpring({
    from: { opacity: 0.5 },
    to: { opacity: 1 },
    loop: { reverse: true },
    config: { duration: 800 },
  });

  const dashStarSpring = {
    ...appear,
    ...spinSparkle,
    ...twinkleSparkle,
    delay: 1400
  }

  const triangleStar1Spring = useSpring({
    ...appear,
    ...pulseSparkle,
    delay: 1500,
  });

  const triangleStar2Spring = useSpring({
    ...appear,
    delay: 1600,
  });

  const triangleStar3Spring = useSpring({
    ...appear,
    ...pulseSparkle,
    delay: 1700,
  });



  const multiColorStar1Spring = useSpring({
    from: { opacity: 0, transform: 'scale(1) rotate(0deg)' },
    to: [
      { opacity: 1 },
      {
        transform: 'scale(1.1) rotate(8deg)',
        opacity: 0.9,
        loop: { reverse: true }
      },
    ],
    config: { duration: 1600 },
    delay: 1800,
  });

  const fadeIn = useSpring({
    from: { opacity: 0 },
    to: { opacity: 1 },
    config: { duration: 600 },
    delay: 1700,
  });
  
  const spin = useSpring({
    from: { transform: 'rotate(0deg)' },
    to: { transform: 'rotate(360deg)' },
    loop: true,
    config: { duration: 7000, easing: easings.easeInOutSine },
  });

  const multiColorStar2Spring = {
    ...fadeIn,
    ...spin
  }

  const multiColorStar3Spring = useSpring({
    from: { opacity: 0 },
    to: [
      { opacity: 1 },
      { opacity: 0.75 },
    ],
    loop: { reverse: true },
    config: { duration: 900 },
    delay: 1900,
  });


  return (
    <div className='Slide__band_role'>
      <animated.h2 className='Slide__band_role__title' style={kidNameSpring}>
        <animated.div className='Slide__band_role__instrument' style={instrumentSpring}>
          <ReactSVG src={instrumentIcon} />
        </animated.div>
        <Textfit className='Slide__band_role__title__text' mode="single" max={32} style={{ maxWidth: '200px' }}>
          {`${kid_name} is a`}
        </Textfit>
      </animated.h2>
      <animated.h1
        className='Slide__band_role__role'
        style={{
          ...bandRoleSpring,
          fontSize: band_role.toLowerCase() !== 'keyboardist' ? '80px' : '55px'
        }}
      >
        {band_role}
      </animated.h1>
      <animated.h4 className='Slide__band_role__join' style={textSpring}>{band_name ? 'in the band' : 'in their band'}</animated.h4>
      <animated.h3 className='Slide__band_role__bandName' style={bandNameSpring}>
        {band_name ? `${band_name}!` : ''}
      </animated.h3>
      <animated.div
        className='Slide__band_role__box'
        style={{
          ...boxSpring,
          height: band_name ? '55vh' : '45vh'
        }}
      />
      {band_name && (
        <animated.div className='Slide__band_role__star__triangle1' style={triangleStar1Spring}>
          <ReactSVG src={triangle_star} />
        </animated.div>
      )}
      {band_name && (
        <animated.div className='Slide__band_role__star__triangle2' style={triangleStar2Spring}>
          <ReactSVG src={triangle_star} />
        </animated.div>
      )}
      <animated.div className='Slide__band_role__star__triangle3' style={triangleStar3Spring}>
        <ReactSVG src={triangle_star} />
      </animated.div>
      {band_name && (
        <animated.div className='Slide__band_role__star__dash' style={dashStarSpring}>
          <ReactSVG src={dash_star} />
        </animated.div>
      )}
      <animated.div className='Slide__band_role__star__multiColor1' style={multiColorStar1Spring}>
        <ReactSVG src={multi_color_star_1} />
      </animated.div>
      <animated.div className='Slide__band_role__star__multiColor2' style={multiColorStar2Spring}>
        <ReactSVG src={multi_color_star_2} />
      </animated.div>
      <animated.div className='Slide__band_role__star__multiColor3' style={multiColorStar3Spring}>
        <ReactSVG src={multi_color_star_3} />
      </animated.div>
      <animated.div className='Slide__intro__sunRays' style={sunRaysSpring}>
        <ReactSVG src={sunRays} />
      </animated.div>
    </div>
  )
}

export default BandRoleSlide;

const DataProps = {
  kid_name: PropTypes.string.isRequired,
  band_role: PropTypes.string.isRequired,
  band_name: PropTypes.string,
  instrument: PropTypes.string.isRequired
}

BandRoleSlide.propTypes = {
  data: PropTypes.shape(DataProps).isRequired
}
